import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetUniversalHeadlines } from '@root/modules/headlines/types/headlines';
import getUniversalHeadlines from '@root/modules/headlines/graphql/getUniversalHeadlines.graphql';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = GetUniversalHeadlines['variables'];

type FetchVariables = GetUniversalHeadlines['variables'];

type Response = GetUniversalHeadlines['data'];

const emptyHeadlines = {
  items: [],
  pager: {
    offset: 0,
    count: 0,
  },
};

// TODO: unify getMostReadArticles with UniversalHeadlines (they are basically the same query)
export default class UniversalHeadlinesService extends Service implements ServiceInterface {
  private async fetchUniversalHeadlines(variables: FetchVariables) {
    try {
      const localVariables = variables;

      // Force exclude ELTA channel in all usage cases
      localVariables.excludeChannels = variables.excludeChannels ? [...variables.excludeChannels, 'www.delfi.lt/elta'] : ['www.delfi.lt/elta'];

      // Initialise relatedMedia variable if not provided to avoid broken queries, as relatedMedia is in query input.
      if (!localVariables.relatedMedia) {
        localVariables.relatedMedia = {};
      }

      const options = Object.assign({ query: getUniversalHeadlines }, { variables: localVariables });
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'universal_headlines',
        },
        cacheKeyFn: (params: Record<string, unknown>) => {
          const key = JSON.stringify(params.variables);
          return key;
        },
      };
      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };
      const cacheData = variables.offset === 0 || !variables.search;
      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader && cacheData ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.headlines?.items.length) {
        throw 'Empty headlines';
      }

      const items = response?.headlines?.items || [];
      const adaptedItems: GetUniversalHeadlines['data']['headlines']['items'] = [];

      // Merge attributes (queried as non-boolean) with settings (queried as boolean)
      items.forEach((item) => {
        if (item.attributes?.length) {
          item.settings = [...item.settings, ...item.attributes];
        }

        delete item.attributes;
        adaptedItems.push(item);
      });

      return {
        items: adaptedItems,
        pager: response?.headlines?.pager,
      };
    } catch {
      return emptyHeadlines;
    }
  }

  public async fetch(variables: ServiceVariables) {
    const response = await this.fetchUniversalHeadlines(variables);

    return response;
  }
}
